'use client'

import { ChevronDown } from 'lucide-react'
import { formatPrice } from '~/utils/format'
import { useState, memo, useEffect } from 'react'
import BookingDetailsPopup from '../booking/mobile/list/booking-details-popup'
import type { RoomBooking } from '~/types/booking'
import { useLocale } from '~/contexts/LocaleContext'
import { getTranslation } from '~/lib/i18n-utils'
import { useBookingCart } from '~/stores/booking-cart'

interface BookingSummaryFooterProps {
  onContinue: () => void
  checkIn: string
  checkOut: string
  checkInTime: string
  checkOutTime: string
  totalRooms: number
  rooms: RoomBooking[]
  totalAmount: number
  onUpdateGuests?: (
    roomId: string,
    adults: number,
    children6To12: number,
    childrenUnder6: number
  ) => void
  isDetail?: boolean
  onHeightChange?: (height: number) => void
}

function BookingSummaryFooter({
  onContinue,
  checkIn,
  checkOut,
  checkInTime,
  checkOutTime,
  totalRooms,
  rooms,
  onUpdateGuests,
  isDetail = false,
}: BookingSummaryFooterProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const { locale } = useLocale()
  const t = getTranslation(locale)
  const { totalAmount } = useBookingCart((state) => ({
    totalAmount: state.totalAmount,
  }))

  const handleShowDetail = () => {
    if (!isDetail) {
      setIsDetailsOpen(true)
    }
  }

  const handleContinue = () => {
    const invalidRoom = rooms.find(
      (room) =>
        room.adults > room.maxAdults || room.children6To12 + room.childrenUnder6 > room.maxChildren
    )
    if (invalidRoom && !isDetail) {
      setIsDetailsOpen(true)
      setTimeout(() => {
        document
          .getElementById(`room-card-${invalidRoom.cartItemId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 350)
      return
    }
    onContinue()
  }

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white p-4 md:hidden">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-1">
              <span className="font-lexend text-[18px] text-lg font-medium leading-6 text-secondary-950">
                {formatPrice(totalAmount)}
              </span>
            </div>
            <button onClick={handleShowDetail} className="flex items-center gap-1">
              <span className="font-lexend text-[12px] text-sm leading-6 text-[#666B74]">
                {t('booking.totalWithTax')}
              </span>
              <ChevronDown className="h-5 w-5 text-[#666B74]" />
            </button>
          </div>
          <button
            onClick={handleContinue}
            className="rounded-[4px] bg-[#8B5E3C] px-8 py-3 font-lexend text-sm font-medium leading-5 text-white transition-colors hover:bg-[#7A4E2C] active:bg-[#694D2B]"
          >
            {t('booking.continue')}
          </button>
        </div>
      </div>

      <BookingDetailsPopup
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        checkIn={checkIn}
        checkOut={checkOut}
        checkInTime={checkInTime}
        checkOutTime={checkOutTime}
        totalRooms={totalRooms}
        rooms={rooms}
        onUpdateGuests={onUpdateGuests || (() => {})}
        onContinue={onContinue}
      />
    </>
  )
}

function arePropsEqual(prevProps: BookingSummaryFooterProps, nextProps: BookingSummaryFooterProps) {
  return (
    prevProps.totalAmount === nextProps.totalAmount &&
    prevProps.totalRooms === nextProps.totalRooms &&
    prevProps.checkIn === nextProps.checkIn &&
    prevProps.checkOut === nextProps.checkOut &&
    prevProps.rooms === nextProps.rooms
  )
}

export default memo(BookingSummaryFooter, arePropsEqual)
